import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  createEstimateContext,
  getSectionContext,
  calculateConstructionDesign,
  saveAndUpdateContext,
  updateContextWithCalculationResults,
  rememberUserEffortsFromEstimate,
  resetRememberedUserEfforts,
  applyRememberedUserEfforts,
  firstNonEmptyList,
  pickUnifiedDayEfforts,
  resolveBimRoleSectionId,
  resolveEffortValue,
  getRememberedUserEfforts,
} from "../../../../api/projectEstimatesApi";

/**
 * Контекст для управления состоянием сметы
 *
 * ОБНОВЛЕНО: Добавлены новые функции для работы с API:
 * - loadEstimateData(projectId) - загрузка всех данных сметы с сервера
 * - loadSectionData(projectId, sectionId) - загрузка данных конкретной секции
 * - performCalculation(projectId) - калькуляция с обновлением контекста
 * - performSave(projectId) - сохранение с обновлением контекста
 *
 * Удалены хардкодированные значения по умолчанию (30, 400000 и т.д.)
 * Все функции используют новый API с правильными эндпоинтами
 */
const getContractSectionsList = (contractSections) => {
  if (!contractSections) return [];
  if (Array.isArray(contractSections.sections)) {
    return contractSections.sections;
  }
  if (Array.isArray(contractSections)) {
    return contractSections;
  }
  return [];
};

const withPreservedContractSections = (estimate, fallbackSections) => {
  if (!estimate || !fallbackSections?.length) return estimate;
  if (getContractSectionsList(estimate.contract_sections).length > 0) {
    return estimate;
  }
  const totalAmount = fallbackSections.reduce(
    (sum, section) => sum + (parseFloat(section.amount) || 0),
    0,
  );
  return {
    ...estimate,
    contract_sections: {
      sections: fallbackSections,
      total_amount: totalAmount,
    },
  };
};

const EstimateStateContext = createContext();

export const useEstimateState = () => {
  const context = useContext(EstimateStateContext);
  if (!context) {
    throw new Error(
      "useEstimateState должен использоваться внутри EstimateStateProvider"
    );
  }
  return context;
};

export const EstimateStateProvider = ({ children }) => {
  // Полный объект сметы
  const [estimateData, setEstimateDataState] = useState(null);

  // Не даём калькуляции и другим вкладкам затирать пользовательские трудозатраты нулём
  const setEstimateData = useCallback((updater) => {
    setEstimateDataState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const applied = applyRememberedUserEfforts(next);
      return applied === prev ? prev : applied;
    });
  }, []);
  // Детали секций (per_block_data источник)
  const [sectionDetails, setSectionDetails] = useState({});

  // Флаг загрузки
  const [isLoading, setIsLoading] = useState(false);

  // Флаг сохранения (предотвращает множественные вызовы)
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  // Порядок разделов для сохранения: WorkTab перед сохранением вызывает setConstructionDesignForSave(orderedList),
  // чтобы performSave отправил актуальный порядок (setState асинхронен, ref даёт гарантию).
  const constructionDesignForSaveRef = useRef(null);
  // То же для разделов на договоре: setState не успевает до performSave.
  const contractSectionsForSaveRef = useRef(null);

  // Ошибка
  const [error, setError] = useState(null);

  // Инициализация данных сметы
  const initializeEstimateData = useCallback((data) => {
    rememberUserEffortsFromEstimate(data);
    setEstimateData(data);
    setError(null);
  }, [setEstimateData]);

  // Загрузка данных сметы с сервера (GET /project_estimates/projects/{project_id}, опционально ?version=)
  const loadEstimateData = useCallback(async (projectId, version = null, options = {}) => {
    const silent = Boolean(options.silent);
    if (!silent) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const context = await createEstimateContext(projectId, version);

      resetRememberedUserEfforts();
      rememberUserEffortsFromEstimate(context.estimate);
      const fallbackSections = Array.isArray(
        options.preserveContractSectionsIfEmpty,
      )
        ? options.preserveContractSectionsIfEmpty
        : [];
      setEstimateData(
        withPreservedContractSections(context.estimate, fallbackSections),
      );
      setSectionDetails(context.sections);
    } catch (err) {
      console.error("Ошибка загрузки данных сметы:", err);
      setError(err.message || "Ошибка загрузки данных сметы");
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [setEstimateData]);

  // Загрузка данных конкретной секции
  const loadSectionData = useCallback(async (projectId, sectionId) => {
    try {
      const context = await getSectionContext(projectId, sectionId);
      setSectionDetails((prev) => ({
        ...prev,
        [sectionId]: context.section,
      }));
      return context.section;
    } catch (err) {
      console.error(`Ошибка загрузки данных секции ${sectionId}:`, err);
      setError(err.message || `Ошибка загрузки данных секции ${sectionId}`);
      throw err;
    }
  }, []);

  // Обновление только определенной части данных сметы
  const updateEstimateSection = useCallback((sectionName, sectionData) => {
    setEstimateData((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        [sectionName]: {
          ...prev[sectionName],
          ...sectionData,
        },
      };
    });
  }, []);

  // Обновление нескольких секций одновременно
  const updateMultipleSections = useCallback((sectionsData) => {
    setEstimateData((prev) => {
      if (!prev) return prev;

      const updated = { ...prev };
      Object.keys(sectionsData).forEach((sectionName) => {
        updated[sectionName] = {
          ...updated[sectionName],
          ...sectionsData[sectionName],
        };
      });

      return updated;
    });
  }, []);

  // Полная замена данных сметы (используется при загрузке с сервера)
  const replaceEstimateData = useCallback((data) => {
    setEstimateData(data);
  }, []);

  // Обновление массива секций (construction design пер-секция данные)
  const updateSections = useCallback((sections) => {
    setEstimateData((prev) =>
      prev ? { ...prev, construction_design: sections } : prev
    );
  }, []);

  // Получение данных определенной секции
  const getSectionData = useCallback(
    (sectionName) => {
      return estimateData?.[sectionName] || null;
    },
    [estimateData]
  );

  // Получение полных данных сметы
  const getFullEstimateData = useCallback(() => {
    return estimateData;
  }, [estimateData]);

  // Вспомогательная функция для получения данных всех секций для калькуляции
  const getCalculationData = useCallback(() => {
    if (!estimateData) return null;

    // section_id: приоритет у role.section_id (API теперь возвращает), иначе — по имени раздела
    const sectionsByName = new Map();
    const addSections = (arr) => {
      (arr || []).forEach((item) => {
        const id = item?.section?.id;
        const name = item?.section?.name?.trim();
        if (id != null && name != null) sectionsByName.set(name, id);
      });
    };
    addSections(estimateData.construction_design);
    addSections(estimateData.construction_design_project);

    const resolveBimSectionId = (role) =>
      resolveBimRoleSectionId(role, sectionsByName);
    const resolveQualitySectionId = (role) => {
      if (role.section_id != null) return role.section_id;
      const r = role.role?.trim();
      if (!r || r === "РО 5" || r === "Норм Контроль" || r === "Норм.контроль")
        return null;
      return sectionsByName.get(r) ?? null;
    };

    return {
      bim_coordination: {
        include_bonuses:
          estimateData.bim_coordination?.include_bonuses !== false,
        average_salary: estimateData.bim_coordination?.average_salary,
        quality_control_percent:
          estimateData.bim_coordination?.quality_control_percent,
        direct_bonus_percent:
          estimateData.bim_coordination?.direct_bonus_percent,
        // В API нет поля bim_role_salaries, используем roles; прокидываем section_id и order_index
        bim_role_salaries: firstNonEmptyList(
          estimateData.bim_coordination?.bim_role_salaries,
          estimateData.bim_coordination?.roles,
        ).map((role, index) => ({
          role: role.role,
          average_salary: role.average_salary ?? 0,
          section_id: resolveBimSectionId(role),
          order_index: index,
        })),
      },
      quality_control: {
        include_bonuses:
          estimateData.quality_control?.include_bonuses !== false,
        average_salary: estimateData.quality_control?.average_salary,
        quality_control_percent:
          estimateData.quality_control?.quality_control_percent,
        direct_bonus_percent:
          estimateData.quality_control?.direct_bonus_percent,
        normative_control_efforts: resolveEffortValue(
          estimateData.quality_control?.normative_control_efforts,
          estimateData.quality_control?.roles?.find(
            (role) =>
              role.role === "Норм Контроль" || role.role === "Норм.контроль",
          )?.day_efforts?.direct,
          getRememberedUserEfforts().normative_control_efforts,
        ),
        vk_control_efforts:
          estimateData.quality_control?.vk_control_efforts ??
          estimateData.quality_control?.roles?.find(
            (role) => role.role === "ВК"
          )?.day_efforts?.direct,
        // В API нет поля quality_control_role_salaries, используем roles; прокидываем section_id и order_index
        // ЗП задаётся только для "Норм Контроль", у остальных ролей — null
        quality_control_role_salaries: firstNonEmptyList(
          estimateData.quality_control?.quality_control_role_salaries,
          estimateData.quality_control?.roles,
        ).map((role, index) => {
          const isNorm = role?.role === "Норм.контроль" || role?.role === "Норм Контроль";
          return {
            role: role.role,
            average_salary: isNorm ? (role.average_salary ?? null) : null,
            section_id: resolveQualitySectionId(role),
            order_index: index,
          };
        }),
      },
      project_delivery: {
        include_bonuses:
          estimateData.project_delivery?.include_bonuses !== false,
        average_salary: estimateData.project_delivery?.average_salary,
        quality_control_percent:
          estimateData.project_delivery?.quality_control_percent,
        direct_bonus_percent:
          estimateData.project_delivery?.direct_bonus_percent,
        project_delivery_role_salaries: firstNonEmptyList(
          estimateData.project_delivery?.project_delivery_role_salaries,
          estimateData.project_delivery?.roles,
        ).map((role, index) => ({
          role: role.role,
          average_salary: role.average_salary ?? 0,
          section_id:
            role.section_id != null
              ? role.section_id
              : sectionsByName.get(role.role?.trim()) ?? null,
          order_index: index,
        })),
      },
      project_management: {
        include_bonuses:
          estimateData.project_management?.include_bonuses !== false,
        quality_control_percent:
          estimateData.project_management?.quality_control_percent,
        direct_bonus_percent:
          estimateData.project_management?.direct_bonus_percent,
        administration_efforts: resolveEffortValue(
          estimateData.project_management?.administration_efforts,
          estimateData.project_management?.roles?.find(
            (role) => role.role === "Администрирование Проекта"
          )?.day_efforts?.direct,
          getRememberedUserEfforts().administration_efforts,
        ),
        // В API нет поля management_role_salaries, используем roles; прокидываем order_index
        management_role_salaries:
          estimateData.project_management?.roles?.map((role, index) => ({
            role: role.role,
            average_salary: role.average_salary,
            coefficient: role.coefficient,
            order_index: index,
          })) || [],
      },
    };
  }, [estimateData]);

  // Построение полного payload для калькуляции, с опциональными переопределениями
  const buildCalculatePayload = useCallback(
    (overrides = {}) => {
      if (!estimateData) return null;

      // Вспомогательная функция для проверки, является ли раздел единым (без блоков)
      const isUnifiedSection = (sectionName) => {
        const name = sectionName?.toLowerCase().trim();
        return name === "инсоляция" || name === "гп";
      };

      const constructionDesignList = estimateData.construction_design || [];

      const per_section_data = constructionDesignList.map((s) => ({
        section: { id: s.section.id, name: s.section.name },
        day_efforts: s.day_efforts,
        money_expenditures: s.money_expenditures,
        different_in_expenditures: s.different_in_expenditures,
        base_price_per_m2: s.base_price_per_m2,
        expenditures_percent: s.expenditures_percent,
        payroll_taxes: s.payroll_taxes ?? s.money_expenditures?.direct ?? 0,
        price_with_payroll_taxes:
          s.price_with_payroll_taxes ??
          s.money_expenditures?.threshold + s.money_expenditures?.direct,
      }));

      // Формируем per_block_data и construction_design_project с order_index по порядку в таблице.
      // Для единых разделов (Инсоляция, ГП) в payload отправляем не более одного вхождения на section_id,
      // иначе дубли в construction_design после калькуляции дают разный payload при повторном blur.
      const per_block_data = [];
      const construction_design_project = [];
      const unifiedSectionIdsInProject = new Set();
      constructionDesignList.forEach((s, order_index) => {
        const det = sectionDetails?.[s.section?.id];
        if (isUnifiedSection(s.section?.name)) {
          const sectionId = s.section?.id;
          if (sectionId != null && unifiedSectionIdsInProject.has(sectionId)) return;
          if (sectionId != null) unifiedSectionIdsInProject.add(sectionId);
          construction_design_project.push({
            section_id: sectionId,
            threshold_percent: s.expenditures_percent?.threshold || 0,
            average_salary: det?.average_salary || 0,
            day_efforts:
              pickUnifiedDayEfforts({
                direct: det?.total_day_efforts,
                total: pickUnifiedDayEfforts(s.day_efforts),
              }) ?? 0,
            order_index: construction_design_project.length,
          });
        } else {
          per_block_data.push({
            section_id: s.section.id,
            threshold_percent: s.expenditures_percent?.threshold,
            average_salary: det?.average_salary,
            blocks:
              det?.block_expenditures?.map((b) => ({
                tei_id: b.block?.tei_id,
                day_efforts: b.day_efforts,
              })) || [],
            order_index,
          });
        }
      });

      // Получаем глобальный процент бонусов из sectionDetails или estimateData
      const bonusFromSections = Object.values(sectionDetails || {})
        .map((det) => det.bonus_percent)
        .filter((val) => val !== undefined && val !== null)
        .find((val) => val > 0);
      
      // Используем значение из секций, если есть, иначе из estimateData, иначе 0
      const globalBonusPercent = 
        bonusFromSections !== undefined 
          ? bonusFromSections 
          : (estimateData.bonus_percent_construction_design ?? 0);

      const payload = {
        per_section_data,
        per_block_data,
        ...(construction_design_project.length > 0 && {
          construction_design_project,
        }),
        ...getCalculationData(),
        bonus_percent_construction_design:
          overrides.bonus_percent_construction_design ?? globalBonusPercent, // 0 допустим, не подменять на fallback
        bonus_percent_aup_aho:
          overrides.bonus_percent_aup_aho ??
          estimateData.bonus_percent_aup_aho ??
          0,
        total_area: estimateData.total_area,
        contract_value: estimateData.contract_value,
        contract_value_construction: estimateData.contract_value_construction,
        contract_value_before_nds: estimateData.contract_value_before_nds,
        cost_price: estimateData.cost_price,
        difference: estimateData.difference,
      };

      // Переопределения (локальные изменения) - исключаем bonus_percent_construction_design, так как он уже установлен выше
      Object.keys(overrides || {}).forEach((k) => {
        if (
          k !== "bonus_percent_construction_design" &&
          k !== "bonus_percent_aup_aho"
        ) {
          payload[k] = { ...(payload[k] || {}), ...(overrides[k] || {}) };
        }
      });

      return payload;
    },
    [estimateData, sectionDetails, getCalculationData]
  );

  // Построение полного payload для сохранения, с опциональными переопределениями
  const buildSavePayload = useCallback(
    (overrides = {}) => {
      if (!estimateData) return null;

      // Вспомогательная функция для проверки, является ли раздел единым (без блоков)
      const isUnifiedSection = (sectionName) => {
        const name = sectionName?.toLowerCase().trim();
        return name === "инсоляция" || name === "гп";
      };

      const constructionDesignList = estimateData.construction_design || [];

      // Формируем construction_design и construction_design_project с order_index по порядку в таблице.
      // Для единых разделов — не более одного вхождения на section_id (как в buildCalculatePayload).
      const construction_design = [];
      const construction_design_project = [];
      const unifiedSectionIdsInProject = new Set();
      // Бэкенд требует threshold_percent > 0 и average_salary > 0; подставляем минимум, если 0 или нет
      const ensurePositive = (val, fallback) =>
        val != null && Number(val) > 0 ? Number(val) : fallback;

      constructionDesignList.forEach((s, order_index) => {
        const det = sectionDetails?.[s.section?.id];
        if (isUnifiedSection(s.section?.name)) {
          const sectionId = s.section?.id;
          if (sectionId != null && unifiedSectionIdsInProject.has(sectionId)) return;
          if (sectionId != null) unifiedSectionIdsInProject.add(sectionId);
          construction_design_project.push({
            section_id: sectionId,
            threshold_percent: ensurePositive(
              s.expenditures_percent?.threshold,
              0.01,
            ),
            average_salary: ensurePositive(det?.average_salary, 1),
            day_efforts:
              pickUnifiedDayEfforts({
                direct: det?.total_day_efforts,
                total: pickUnifiedDayEfforts(s.day_efforts),
              }) ?? 0,
            order_index: construction_design_project.length,
          });
        } else {
          construction_design.push({
            section_id: s.section.id,
            threshold_percent: ensurePositive(
              s.expenditures_percent?.threshold,
              0.01,
            ),
            average_salary: ensurePositive(det?.average_salary, 1),
            blocks:
              det?.block_expenditures?.map((b) => ({
                tei_id: b.block?.tei_id,
                day_efforts: b.day_efforts,
              })) || [],
            order_index,
          });
        }
      });

      // Формируем contract_sections из estimateData
      // Поддержка новой структуры: contract_sections.sections или старого массива
      let contractSectionsArray = [];
      if (estimateData.contract_sections) {
        if (
          estimateData.contract_sections.sections &&
          Array.isArray(estimateData.contract_sections.sections)
        ) {
          // Новая структура: объект с полем sections
          contractSectionsArray = estimateData.contract_sections.sections;
        } else if (Array.isArray(estimateData.contract_sections)) {
          // Старая структура: массив
          contractSectionsArray = estimateData.contract_sections;
        }
      }

      const contract_sections = contractSectionsArray
        .map((cs) => ({
          section_id: cs.section?.id || cs.section_id,
          amount: cs.amount || 0,
        }))
        .filter((cs) => cs.section_id != null);

      // Приоритет у согласованного значения из секций (актуально после калькуляции/редактирования),
      // иначе — estimateData (при загрузке может быть старым)
      const bonusValues = Object.values(sectionDetails || {})
        .map((det) => det.bonus_percent)
        .filter((val) => val !== undefined && val !== null);
      const allSameBonus =
        bonusValues.length > 0 && bonusValues.every((v) => v === bonusValues[0]);
      // Берём первое ненулевое значение из секций, если все значения одинаковые
      // Если значения разные - используем первое ненулевое или fallback на estimateData
      const firstNonZeroBonus = bonusValues.find((v) => v > 0);
      const fromSections = allSameBonus 
        ? bonusValues[0] 
        : (firstNonZeroBonus !== undefined ? firstNonZeroBonus : undefined);
      const bonusPercent =
        fromSections !== undefined
          ? fromSections
          : (estimateData.bonus_percent_construction_design ?? 0);

      const base = {
        construction_design,
        construction_design_project, // Всегда включаем, даже если массив пустой (бэкенд требует это поле)
        ...(contract_sections.length > 0 && {
          contract_sections,
        }),
        ...getCalculationData(),
        bonus_percent_construction_design: bonusPercent,
        bonus_percent_aup_aho: estimateData.bonus_percent_aup_aho ?? 0,
        // Прокидываем заголовочные поля, если бэкенд их принимает при сохранении
        contract_value: estimateData.contract_value,
        contract_value_before_nds: estimateData.contract_value_before_nds,
        contract_value_construction: estimateData.contract_value_construction,
        cost_price: estimateData.cost_price,
        difference: estimateData.difference,
        total_area: estimateData.total_area,
      };

      Object.keys(overrides || {}).forEach((k) => {
        if (
          k === "bonus_percent_construction_design" ||
          k === "bonus_percent_aup_aho"
        ) {
          base[k] = overrides[k];
          return;
        }
        base[k] = { ...(base[k] || {}), ...(overrides[k] || {}) };
      });

      return base;
    },
    [estimateData, sectionDetails, getCalculationData]
  );

  // Функция для получения данных калькуляции с учетом локальных изменений
  const getCalculationDataWithLocalChanges = useCallback(
    (sectionName, localValues) => {
      if (!estimateData) return null;

      const baseData = getCalculationData();
      if (!baseData) return null;

      // Если переданы локальные значения для конкретной секции, используем их
      if (sectionName && localValues) {
        return {
          ...baseData,
          [sectionName]: {
            ...baseData[sectionName],
            ...localValues,
          },
        };
      }

      return baseData;
    },
    [estimateData, getCalculationData]
  );

  // Функция калькуляции с обновлением контекста
  const performCalculation = useCallback(
    async (projectId) => {
      if (!estimateData) {
        console.error("Нет данных сметы для калькуляции");
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await calculateConstructionDesign(
          projectId,
          estimateData,
          sectionDetails
        );

        // Обновляем контекст с результатами калькуляции
        console.log(
          "EstimateContext: performCalculation - обновляем контекст с результатами",
          result
        );
        const updatedContext = updateContextWithCalculationResults(
          { estimate: estimateData, sections: sectionDetails },
          result
        );

        console.log(
          "EstimateContext: performCalculation - обновленный контекст",
          updatedContext
        );
        setEstimateData(updatedContext.estimate);
        setSectionDetails(updatedContext.sections);

        return result;
      } catch (err) {
        console.error("Ошибка калькуляции:", err);
        setError(err.message || "Ошибка калькуляции");
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [estimateData, sectionDetails]
  );

  // Установить порядок разделов для следующего сохранения (вызывается из WorkTab.syncDataBeforeSave)
  const setConstructionDesignForSave = useCallback((orderedList) => {
    constructionDesignForSaveRef.current = Array.isArray(orderedList)
      ? orderedList
      : null;
  }, []);

  const setContractSectionsForSave = useCallback((contractSections) => {
    contractSectionsForSaveRef.current = contractSections ?? null;
  }, []);

  // Функция сохранения с обновлением контекста
  const performSave = useCallback(
    async (projectId) => {
      if (!estimateData) {
        console.error("Нет данных сметы для сохранения");
        return null;
      }

      // Предотвращаем множественные вызовы с помощью useRef
      if (isSavingRef.current) {
        return null;
      }

      isSavingRef.current = true;
      setIsSaving(true);
      setError(null);

      try {
        // Используем порядок из ref, если WorkTab вызвал setConstructionDesignForSave перед сохранением
        const orderedDesign = constructionDesignForSaveRef.current;
        const contractSectionsForSave = contractSectionsForSaveRef.current;
        const fromRef = getContractSectionsList(contractSectionsForSave);
        const fromEstimate = getContractSectionsList(
          estimateData.contract_sections,
        );
        // Если вкладка синхронизировалась — берём её список (в т.ч. пустой).
        // Иначе не теряем разделы из GET/контекста.
        const fallbackSections =
          contractSectionsForSave != null ? fromRef : fromEstimate;
        const baseEstimate =
          orderedDesign != null && orderedDesign.length > 0
            ? {
                ...estimateData,
                construction_design: orderedDesign.map((item, index) => ({
                  ...item,
                  order_index: index,
                })),
                construction_design_project: [], // единый список уже в construction_design, не дублировать
              }
            : estimateData;
        const dataToSave = applyRememberedUserEfforts({
          ...baseEstimate,
          ...(fallbackSections.length > 0 && {
            contract_sections: {
              sections: fallbackSections,
              total_amount: fallbackSections.reduce(
                (sum, section) => sum + (parseFloat(section.amount) || 0),
                0,
              ),
            },
          }),
        });

        const result = await saveAndUpdateContext(
          projectId,
          dataToSave,
          sectionDetails
        );

        // Обновляем контекст с сохраненными данными
        setEstimateData(
          withPreservedContractSections(
            result.context.estimate,
            fallbackSections,
          ),
        );
        setSectionDetails(result.context.sections);

        return result.saveResult;
      } catch (err) {
        console.error("Ошибка сохранения:", err);
        setError(err.message || "Ошибка сохранения");
        throw err;
      } finally {
        constructionDesignForSaveRef.current = null;
        contractSectionsForSaveRef.current = null;
        isSavingRef.current = false;
        setIsSaving(false);
      }
    },
    [estimateData, sectionDetails, setEstimateData]
  );

  const value = {
    estimateData,
    isLoading,
    isSaving,
    error,
    initializeEstimateData,
    loadEstimateData,
    loadSectionData,
    updateEstimateSection,
    updateMultipleSections,
    replaceEstimateData,
    updateSections,
    getSectionData,
    getFullEstimateData,
    getCalculationData,
    getCalculationDataWithLocalChanges,
    sectionDetails,
    setSectionDetails,
    setEstimateData,
    setConstructionDesignForSave,
    setContractSectionsForSave,
    buildCalculatePayload,
    buildSavePayload,
    performCalculation,
    performSave,
    setIsLoading,
    setError,
  };

  return (
    <EstimateStateContext.Provider value={value}>
      {children}
    </EstimateStateContext.Provider>
  );
};
